import type {
  BarometerSuccess,
} from '@autobid/strapi-integration/typescript/auction/barometerCar'
import type {
  CarsListSuccess,
} from '@autobid/strapi-integration/typescript/auction/carsList'
import type {
  DetailsSuccess,
} from '@autobid/strapi-integration/typescript/auction/detailsCar'
import type {
  SearchListSuccess,
} from '@autobid/strapi-integration/typescript/auction/searchCars'
import type { AnnouncementCar, CarsListCar, SearchListCar } from '@autobid/ui/types/Car'
import {
  auctionCarsQueryKey,
} from '@autobid/ui/composables/car/data/useAuctionCars'
import {
  getBarometerQueryKey,
} from '@autobid/ui/composables/car/data/useBarometerCar'
import {
  carDetailsQueryKey,
} from '@autobid/ui/composables/car/data/useDetailsCar'
import {
  searchCarsQueryKey,
} from '@autobid/ui/composables/car/data/useSearchCars'
import { updateNonStaleQueries } from '@autobid/ui/utils/updateNonStaleQueries'
import { updateNonStaleQuery } from '@autobid/ui/utils/updateNonStaleQuery'
import { useQueryClient } from '@tanstack/vue-query'
import { announcementCarQueryKey } from './data/useCurrentCarAnnouncement'

export function useUpdateCar() {
  const queryClient = useQueryClient()

  const updateAuctionCar = (
    /**
     * Pass 'unknown' if you do not have an access to the auctionId
     */
    auctionId: number | 'unknown',
    carId: number,
    updater: (data: CarsListCar) => CarsListCar,
  ) => {
    const queryKey
      = auctionId === 'unknown'
        ? auctionCarsQueryKey.all()
        : auctionCarsQueryKey.byAuctionId(auctionId)

    const allQueries = queryClient.getQueryCache().findAll(queryKey)

    allQueries.forEach((query) => {
      const wasStale = query.isStale() // call it before the update
      queryClient.setQueryData<CarsListSuccess['data']['items']>(query.queryKey, (data) => {
        if (!data)
          return data

        const updated = data.items.map((item: CarsListCar) => {
          if (item.id === carId) {
            return {
              ...updater(item),
            }
          }
          return item
        })
        return { ...data, items: updated }
      })
      if (wasStale) {
        queryClient.invalidateQueries({
          queryKey: query.queryKey,
          refetchType: 'none',
        })
      }
    })
  }

  const updateSearchCar = (
    carId: number,
    updater: (data: SearchListCar) => SearchListCar,
  ) => {
    const queryKey = searchCarsQueryKey.all()

    const allQueries = queryClient.getQueryCache().findAll(queryKey)

    allQueries.forEach((query) => {
      const wasStale = query.isStale() // call it before the update
      queryClient.setQueryData<SearchListSuccess['data']['items']>(query.queryKey, (data) => {
        if (!data)
          return data

        const updated = data.items.map((item) => {
          if (item.id === carId) {
            return {
              ...updater(item),
            }
          }
          return item
        })
        return { ...data, items: updated }
      })
      if (wasStale) {
        queryClient.invalidateQueries({
          queryKey: query.queryKey,
          refetchType: 'none',
        })
      }
    })
  }

  const updateDetailsCarData = (
    carId: number,
    updater: (
      data: DetailsSuccess['data']['singleItem']['item']['itemData']
    ) => DetailsSuccess['data']['singleItem']['item']['itemData'],
  ) => {
    const detailsQueryKey = carDetailsQueryKey.byCarId(carId)

    queryClient.setQueryData(
      detailsQueryKey,
      (data?: DetailsSuccess['data']['singleItem']) => {
        if (!data)
          return data

        return {
          ...data,
          item: {
            ...data.item,
            itemData: {
              ...updater(data.item.itemData),
            },
          },
        }
      },
    )
  }

  const updateBarometerCarData = (
    /**
     * Pass undefined if you don't want to update the current car. Pass 'unknown' if you do not have an access to the auctionId
     */
    auctionId: number | undefined | 'unknown',
    carId: number,
    updater: (
      data: BarometerSuccess['data']['singleItem']['item']['itemData']
    ) => BarometerSuccess['data']['singleItem']['item']['itemData'],
  ) => {
    const getQueryKeys = () => {
      const barometerKey = getBarometerQueryKey({
        type: 'item',
        carId,
      })

      if (auctionId === 'unknown') {
        return [
          barometerKey,
          getBarometerQueryKey({
            type: 'current',
          }),
        ]
      }

      if (typeof auctionId === 'number') {
        return [
          barometerKey,
          getBarometerQueryKey({
            type: 'current',
            auctionId,
          }),
        ]
      }

      return [barometerKey]
    }

    const queryKeys = getQueryKeys()

    for (const queryKey of queryKeys) {
      updateNonStaleQueries(queryClient, queryKey, (data?: BarometerSuccess['data']['singleItem']) => {
        if (!data)
          return data

        if (data.item.itemData.id !== carId)
          return data

        return {
          ...data,
          item: {
            ...data.item,
            itemData: {
              ...updater(data.item.itemData),
            },
          },
        }
      })
    }
  }

  const updateAnnouncementCarData = (
    auctionId: number,
    updater: (
      data?: AnnouncementCar
    ) => AnnouncementCar | undefined,
  ) => {
    const queryKey = announcementCarQueryKey.byAuctionId(auctionId)

    updateNonStaleQuery(queryClient, queryKey, updater)
  }

  return {
    updateAuctionCar,
    updateSearchCar,
    updateDetailsCarData,
    updateBarometerCarData,
    updateAnnouncementCarData,
  }
}
